import '../utils/shims';
import React from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { CookiesProvider, Cookies } from 'react-cookie';
import { hydrateRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { renderRoutes } from 'react-router-config';
import { BrowserRouter } from 'react-router-dom';

import {
    AmplitudeProvider,
} from 'wanderu-analytics';
import appSettings, { setConfig } from 'wanderu-config';
import { initDiscountData } from 'wanderu-discount';
import {
    partnerActions,
} from 'wanderu-ducks-partner-data';
import {
    selectCurrency,
    selectLocale,
} from 'wanderu-i18n';
import Logger from 'wanderu-logger';
import { discountCodeSet } from 'wanderu-orders';
import { AccountService } from 'wanderu-service-account';
import {
    affiliate,
    SnowplowService,
    identifyEmailClickSessionIfPresent,
    initUserEvents,
    getFacebookPixelCookies,
} from 'wanderu-service-analytics';
import WapiService, {
    initService as initWapiService,
} from 'wanderu-service-wapi';
import { deserialize, getCookieOptions } from 'wanderu-utils';

import baseAppConfig from '../config/baseApp.locales';
import App, { hydrate as hydrateApp } from '../containers/App';
import initialRoutes from '../routes';
import configureStore from '../utils/configureStore';
import { polyfillIntlWithLocales } from '../utils/intlPolyfill';

const logger = Logger.getLogger('client');

function render(routes, store, cookies) {
    hydrateRoot(
        document.getElementById('root'),
        <BrowserRouter>
            <CookiesProvider cookies={cookies}>
                <Provider store={store}>
                    <AmplitudeProvider>
                        {renderRoutes(routes)}
                    </AmplitudeProvider>
                </Provider>
            </CookiesProvider>
        </BrowserRouter>,
    );
}

async function init() {
    const windowSettings = deserialize(window.appSettings);
    setConfig({
        config: windowSettings.config,
        env: {
            ...windowSettings.env,
            isClient: true,
            isServer: false,
        },
    });

    Logger.config.useConfig(appSettings.config.logging.client);
    Logger.config.addMetadata('build.version', () => appSettings.env.build.version);
    Logger.config.addMetadata('build.hash', () => appSettings.env.build.gitHash);
    Logger.config.addMetadata('userAgent', () => navigator.userAgent);
    Logger.config.addMetadata('cookiesEnabled', () => navigator.cookieEnabled);
    Logger.config.addMetadata('url', () => window.location.href);
    Logger.config.addMetadata('app', () => 'ui-react (client)');
    Logger.config.addMetadata('snowplowIds', () => ({
        networkUserId: SnowplowService.getNetworkUserId(),
        ...SnowplowService.getDomainUserInfo(),
    }));

    datadogRum.init({
        applicationId: '698261cf-4a16-4573-8912-599d76ad9094',
        clientToken: 'pub2a003a53cce099fd6639762628a1a492',
        defaultPrivacyLevel: 'mask-user-input',
        enableExperimentalFeatures: ['clickmap'],
        env: appSettings.env.wanderuEnv,
        service: 'ui-react',
        sessionReplaySampleRate: 2,
        sessionSampleRate: 2,
        site: 'datadoghq.com',
        trackFrustrations: true,
        trackInteractions: true,
        trackUserInteractions: true,
        trackViewsManually: true,
        // Env-gated like every other cookie (wanderu/ui-react#1173): Secure
        // everywhere but plain-http local runs.
        useSecureSessionCookie: getCookieOptions().secure,
        version: appSettings.env.build.version,
    });

    window.addEventListener('pagehide', Logger.sendBeacon);
    window.onerror = function handleError(message, url, line, col, error) {
        if (message.toLowerCase() === 'script error.' || message.toLowerCase().includes('can\'t find variable: $') || message.toLowerCase().includes('$ is not defined')) {
            logger.warn('uncaughtError', message, { line, stack: error.stack, url });
        } else {
            logger.error('uncaughtError', message, { line, stack: error.stack, url });
        }

        return false;
    };

    // eslint-disable-next-line no-underscore-dangle
    const appState = deserialize(window.__INITIAL_STATE__);
    const store = configureStore(appState);
    const cookies = new Cookies();
    const { affiliateId } = initAffiliate(cookies, store);
    initDiscount(cookies, store);

    initSnowplowAnalytics(store);
    initWapiService({ affiliateId });
    // Register the federated re-auth seam (R3): on a federated 401 the WAPI
    // layer defers to the account layer for a single silent Google re-auth
    // instead of re-POSTing the dead provider token.
    WapiService.onReauthenticate(AccountService.reauthenticateFederatedSession);

    // Check if polyfill required
    if (!window.Intl) {
        const locales = Object.values(baseAppConfig.localeMap);
        await polyfillIntlWithLocales(locales);
    }

    // eslint-disable-next-line no-underscore-dangle
    const i18nData = deserialize(window.__INITIAL_I18N__);

    hydrateApp(i18nData);
    await App.clientFetchData(i18nData);

    render(initialRoutes, store, cookies);

    return store;
}

function initAffiliate(cookies, store) {
    const {
        affiliateId,
        trackingId,
    } = affiliate.initAffiliateData(cookies);

    if (affiliateId) {
        store.dispatch(partnerActions.setPartnerDetails({ affiliateId, trackingId }));
    }

    return { affiliateId, trackingId };
}

function initDiscount(cookies, store) {
    try {
        const { discountCode } = initDiscountData(cookies);

        if (discountCode) {
            store.dispatch(discountCodeSet(discountCode));
        }
    } catch (e) {
        logger.error('discountInitFailed', e);
    }
}

async function initSnowplowAnalytics(store) {
    const { region, version, gitHash } = appSettings.env.build;
    // Captured here, at page load, because onSnowplowLoad can fire up to 30s later
    // (fetchSDK waits on the collector-set sp cookie) and any navigation in between
    // strips bsft_uid from the live URL.
    const landingSearch = window.location.search;
    try {
        initUserEvents(store);
        SnowplowService.init(region, version, gitHash);
        SnowplowService.fetchSDK(() => onSnowplowLoad(store, landingSearch));
        SnowplowService.createNewTracker('ui_react');
        SnowplowService.startTracking();
    } catch {
        logger.error('snowplowTrackingFailed');
    }
}

async function onSnowplowLoad(store, landingSearch) {
    // Awaited: the identify below POSTs to wapi immediately (getIds() resolves at once
    // in this callback), and without the await it would go out before setSession() has
    // cached the auth token — costing a 401 retry at best. Failure is logged and not
    // rethrown: everything after this point is best-effort analytics.
    try {
        await WapiService.startNewSession(undefined, { preserveSessionId: true });
    } catch (e) {
        logger.warn('startNewSessionFailed', e);
    }

    // Identify an email-driven session so Blueshift can stitch the anonymous Snowplow
    // cookie to the recipient of the clicked email (strategy#418). Fired here because
    // this is the first point where both preconditions hold: the tracker is loaded (so
    // getIds() can resolve the Snowplow cookies) and startNewSession above has
    // established the WAPI session the events POST needs. Returns null without doing
    // anything when the landing URL carried no bsft_uid — almost every load.
    const state = store.getState();
    identifyEmailClickSessionIfPresent({
        currency: selectCurrency(state),
        locale: selectLocale(state),
        search: landingSearch,
    }).then((outcome) => {
        // Only a real attempt that failed is worth a line; null means there was no
        // email-driven session to report.
        if (outcome === 'failed') {
            logger.warn('emailClickIdentifyFailed');
        }
    });

    const facebookPixelCookies = await getFacebookPixelCookies();
    if (!facebookPixelCookies) {
        return;
    }

    SnowplowService.fireStructuredEvent({
        action: 'loaded',
        category: 'facebook_pixel',
        property: {
            fbc: facebookPixelCookies.fbc,
            fbp: facebookPixelCookies.fbp,
        },
    });
}

const store = init();

export { store };
